import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useLayoutEffect, useRef } from 'react';
import BreadCrumbs from '@/components/landing/common/Breadcrumb';

/* -------------------------------------------------------------------------- */
/*  Spiral config                                                             */
/* -------------------------------------------------------------------------- */

const ARMS = 2;             // interleaved spiral arms
const CARDS_PER_ARM = 24;   // cards on each arm (one "slot" = 1 / CARDS_PER_ARM of the spiral)
const TURNS = 2;            // revolutions from center to edge (keep it an integer)
const INNER_RATIO = 1 / 6;  // inner radius as a fraction of the outer radius
const OVERSHOOT = 1.25;     // spiral ends this far past the corners

const CARD_W = 220;         // full-size card, cards are only ever scaled DOWN (stays crisp)
const CARD_H = 176;

const AUTO_SPEED = 0.06;      // slots / second while idle (slow ambient drift)

// Scroll motion. Moving along the spiral is a rotation AND a zoom at once.
// SCROLL_GAIN sets how far one px of scroll travels (more = more zoom), and
// SPIN_KEEP cancels part of the rotation so scrolling reads as a zoom, not a spin.
const SCROLL_GAIN = 0.006;    // slots per px scrolled
const SPIN_KEEP = 0.4;        // fraction of the natural rotation kept while scrolling (0 = pure zoom, 1 = full spin)
const SCROLL_MAX_PENDING = 4; // clamp so anchor jumps / scroll restoration can't whip it around

// Critically damped ease-in / ease-out (no overshoot, no rebound):
// speed ramps up smoothly toward SCROLL_RESPONSE * remaining distance, then
// glides to a stop. SCROLL_ACCEL = 4 * SCROLL_RESPONSE is exactly critical damping;
// keep it >= that to guarantee the motion never overshoots its target.
const SCROLL_RESPONSE = 3;    // lower = longer, floatier glide
const SCROLL_ACCEL = 12;      // lower = slower ease-in

const SCROLL_IDLE_MS = 140;   // how long after the last scroll movement auto-spiral resumes
const AUTO_RESUME = 1.2;      // how quickly auto-spiral eases back in after scrolling

const PALETTE = [
  'rgba(239, 83, 80, 0.2)',    // Coral red
  'rgba(236, 144, 66, 0.2)',   // Burnt orange
  'rgba(218, 183, 74, 0.2)',   // Golden yellow
  'rgba(139, 185, 91, 0.2)',   // Lime green
  'rgba(72, 177, 128, 0.2)',   // Emerald
  'rgba(54, 178, 170, 0.2)',   // Teal
  'rgba(65, 155, 205, 0.2)',   // Sky blue
  'rgba(83, 126, 210, 0.2)',   // Cobalt
  'rgba(125, 108, 207, 0.2)',  // Violet
  'rgba(165, 105, 198, 0.2)',  // Purple
  'rgba(207, 105, 160, 0.2)',  // Rose
  'rgba(220, 113, 125, 0.2)',  // Salmon
  'rgba(133, 147, 164, 0.2)',  // Slate
  'rgba(104, 164, 179, 0.2)',  // Steel blue
  'rgba(166, 155, 113, 0.2)',  // Sand
  'rgba(128, 166, 143, 0.2)',  // Sage
];
const icons = [
  { src: "/integrations/nodejs-icon-alt.svg", alt: "JS" },
  { src: "/integrations/jupyter.svg", alt: "Jupyter" },
  { src: "/integrations/python.svg", alt: "PY" },
  { src: "/integrations/model-context-protocol-icon.svg", alt: "MCP" },
  { src: "/integrations/github-copilot.svg", alt: "GH" },
  { src: "/integrations/pypi.svg", alt: "PYPI" },
  { src: "/integrations/tableau-icon.svg", alt: "tableau" },
  { src: "/integrations/excel-icon.svg", alt: "excel" },
  { src: "/integrations/c-plusplus.svg", alt: "C" },
  { src: "/integrations/numpy.svg", alt: "numpy" },
  { src: "/integrations/github-icon.svg", alt: "GH" },
  { src: "/integrations/deepseek-icon.svg", alt: "Deepseek" },
  { src: "/integrations/vim.svg", alt: "Vim" },
  { src: "/integrations/pandas-icon.svg", alt: "pandas" },
  { src: "/integrations/airtable.svg", alt: "Airtable" },
  { src: "/integrations/openai-icon.svg", alt: "OpenAI" },
  { src: "/integrations/pytorch-icon.svg", alt: "pytorch" },
  { src: "/integrations/microsoft-power-bi.svg", alt: "powerbi" },
  { src: "/integrations/sheets-icon.svg", alt: "sheets" },
  { src: "/integrations/npm-icon.svg", alt: "NPM" },
  { src: "/integrations/claude-icon.svg", alt: "Claude" },
  { src: "/integrations/gitlab-icon.svg", alt: "Gitlab" },
  { src: "/integrations/vscode.svg", alt: "VSCode" },
  { src: "/integrations/vercel-icon.svg", alt: "Vercel" },
  { src: "/integrations/notion-icon.svg", alt: "Notion" },
]

const TWO_PI = Math.PI * 2;
const TOTAL_CARDS = ARMS * CARDS_PER_ARM;
const LOG_RATIO = Math.log(1 / INNER_RATIO);
const ANGLE_PER_SLOT = (TURNS * TWO_PI) / CARDS_PER_ARM;
const CARD_BOUND_RADIUS = Math.hypot(CARD_W, CARD_H) / 2; // for off-screen culling

// Static card data. Position / rotation / scale / opacity are written straight
// to the DOM every frame (see the effect below), so React never re-renders them.
// Everything that doesn't change per frame is precomputed here.
const cards = Array.from({ length: TOTAL_CARDS }, (_, i) => ({
  color: PALETTE[i % PALETTE.length],
  icon: icons[i % icons.length],
  slot: i % CARDS_PER_ARM,
  armAngle: (Math.floor(i / CARDS_PER_ARM) / ARMS) * TWO_PI,
}));

const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/* -------------------------------------------------------------------------- */
/*  Styles                                                                    */
/* -------------------------------------------------------------------------- */

const Wrapper = styled.section`
  position: relative;
  width: 100%;
  height: 720px;
  overflow: hidden;
  border-bottom: 1px solid rgba(var(--greyr), 0.1);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 70% 65% at 50% 60%,
      rgba(255, 255, 255, 0.99) 0%,
      rgba(255, 255, 255, 0.75) 50%,
      rgba(255, 255, 255, 0) 92%
    );
    pointer-events: none;
  }
`;

const Cards = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  pointer-events: none;
`;

/* One element per card, positioned/transformed every frame by the effect below.
   Only transform + opacity ever change, so each card is promoted to its own
   compositor layer, rasterized once, and then just moved by the GPU.
   The card is centered with top/left calc() so the per-frame transform string
   doesn't need a translate(-50%, -50%) step. */
const Card = styled.div<{ $color: string }>`
  position: absolute;
  top: calc(50% - ${CARD_H / 2}px);
  left: calc(50% - ${CARD_W / 2}px);
  width: ${CARD_W}px;
  height: ${CARD_H}px;
  background: ${({ $color }) => $color};
  border-radius: 24px;
  box-shadow: 0 36px 72px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  will-change: transform, opacity;
  backface-visibility: hidden;

  img {
    width: auto;
    height: 40%;
    opacity: 0.3;
    user-select: none;
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 24px;
`;

const Heading = styled.h1`
  font-size: 56px;
  line-height: 1.1;
  margin: 0 0 32px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const SignUpButton = styled.button`
  background: #1a1a1a;
  color: #ffffff;
  border: none;
  padding: 14px 28px;
  font-size: 15px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.3s;
  &:hover {
    background: #333;
  }
`;

const GetAppButton = styled.button`
  background: #ffffff;
  color: #1a1a1a;
  border: none;
  padding: 14px 28px;
  font-size: 15px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.3s;
  &:hover {
    background: #eee;
  }
`;

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

const SpiralHeader = () => {
  const navigate = useNavigate();

  const wrapperRef = useRef<HTMLElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const els = cardRefs.current;

    const autoSpeed = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 0
      : AUTO_SPEED;

    // Layout (updated by ResizeObserver, never read inside the frame loop)
    let rMax = 1;
    let sizeK = 1;
    const measure = () => {
      const { width, height } = wrapper.getBoundingClientRect();
      rMax = Math.hypot(width / 2, height / 2) * OVERSHOOT; // spiral ends just past the corners
      sizeK = Math.min(1, Math.max(0.55, width / 1200));     // smaller cards on narrow screens
    };

    // Motion state
    let progress = 0;       // position along the spiral, in "slots"
    let pending = 0;        // scroll-requested slots not yet applied
    let scrollSpeed = 0;    // current scroll-driven speed, slots / second (eased)
    let spinOffset = 0;     // angle that cancels part of the rotation caused by scrolling
    let lastScrollY = window.scrollY;
    let lastScrollAt = -Infinity;
    let autoBlend = 1;      // 0 = scroll in control, 1 = auto-spiral in control
    let last = -1;
    let raf = 0;

    // Last opacity written to the DOM, so unchanged styles are never rewritten
    const lastOpacity: number[] = new Array(TOTAL_CARDS).fill(-1);

    const render = () => {
      const rMin = rMax * INNER_RATIO;
      const cullR = rMax / OVERSHOOT; // distance from center to the wrapper's corners

      for (let i = 0; i < TOTAL_CARDS; i++) {
        const el = els[i];
        if (!el) continue;
        const card = cards[i];

        // u: 0 = inner end of the spiral, 1 = outer end. Wraps forever.
        let s = card.slot + progress;
        if (s >= CARDS_PER_ARM) s -= CARDS_PER_ARM;
        const u = s / CARDS_PER_ARM;

        // Logarithmic spiral: radius grows exponentially with angle, so moving
        // along it is a rotation and a zoom at the same time.
        const r = rMin * Math.exp(u * LOG_RATIO);

        // Shrink to nothing at the center, fade at the far edge, so the wrap is invisible
        const fadeIn = smoothstep(0, 0.12, u);
        const fadeOut = 1 - smoothstep(0.94, 1, u);
        const scale = (r / rMax) * sizeK * fadeIn;
        let opacity = fadeIn * fadeOut;

        // Skip cards that can't be seen: invisible, or entirely outside the hero
        if (opacity < 0.004 || r - CARD_BOUND_RADIUS * scale > cullR) opacity = 0;

        if (opacity !== lastOpacity[i]) {
          lastOpacity[i] = opacity;
          el.style.opacity = String(opacity);
        }
        if (opacity === 0) continue;

        const theta = u * TURNS * TWO_PI + card.armAngle + spinOffset;
        const x = r * Math.cos(theta);
        const y = r * Math.sin(theta);

        el.style.transform =
          `translate3d(${x}px, ${y}px, 0) rotate(${theta}rad) scale(${scale})`;
      }
    };

    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);

      // First frame after (re)starting: just sync clocks, no jump
      if (last < 0) {
        last = now;
        lastScrollY = window.scrollY;
        return;
      }
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      if (dt <= 0) return;

      // --- scroll input: sampled once per frame, in sync with rendering ----
      const y = window.scrollY;
      const dy = y - lastScrollY;
      lastScrollY = y;
      if (dy !== 0) {
        pending = clamp(pending + dy * SCROLL_GAIN, -SCROLL_MAX_PENDING, SCROLL_MAX_PENDING);
        lastScrollAt = now;
      }
      const scrolling = now - lastScrollAt < SCROLL_IDLE_MS;

      // --- scroll easing: critically damped, so it eases IN, glides, and stops
      // without overshooting or bouncing back. Speed chases (remaining distance
      // * SCROLL_RESPONSE); the distance covered is taken off `pending`.
      scrollSpeed += (pending * SCROLL_RESPONSE - scrollSpeed) * (1 - Math.exp(-dt * SCROLL_ACCEL));
      let consumed = scrollSpeed * dt;
      pending -= consumed;
      if (Math.abs(pending) < 1e-4 && Math.abs(scrollSpeed) < 1e-3) {
        pending = 0;
        scrollSpeed = 0;
        consumed = 0;
      }

      // Cancel part of the rotation that scrolling would cause, so it reads as zoom
      if (consumed !== 0) {
        spinOffset = (spinOffset - (1 - SPIN_KEEP) * consumed * ANGLE_PER_SLOT) % TWO_PI;
      }

      // Auto-spiral fades out while scrolling, eases back in once you stop
      autoBlend += ((scrolling ? 0 : 1) - autoBlend) * (1 - Math.exp(-dt * AUTO_RESUME));

      const delta = consumed + autoSpeed * autoBlend * dt;
      if (delta === 0) return; // fully idle (e.g. reduced motion): nothing to draw

      progress = (((progress + delta) % CARDS_PER_ARM) + CARDS_PER_ARM) % CARDS_PER_ARM;
      render();
    };

    // The loop only runs while the hero is on screen
    const start = () => {
      if (raf) return;
      last = -1;
      raf = requestAnimationFrame(tick);
    };
    const stop = () => {
      cancelAnimationFrame(raf);
      raf = 0;
    };

    const resizeObserver = new ResizeObserver(() => {
      measure();
      render();
    });
    resizeObserver.observe(wrapper);

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? start() : stop()),
      { rootMargin: '100px 0px' },
    );
    intersectionObserver.observe(wrapper);

    measure();
    render();
    start();

    return () => {
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, []);

  return (
    <Wrapper ref={wrapperRef}>
      <Cards>
        {cards.map((card, i) => (
          <Card
            key={`card-${i}`}
            $color={card.color}
            ref={(el) => { cardRefs.current[i] = el; }}
          >
            <img
              src={card.icon.src}
              alt={card.icon.alt}
              width="40"
              height="40"
              decoding="async"
              draggable={false}
            />
          </Card>
        ))}
      </Cards>

      <Content>
        <BreadCrumbs
          crumbs={[
            {name: 'Developers',path: '/developers'},
            {name: 'SDKs',path: '/developers/libraries'},
          ]}
        />
        <Heading className="serif">
          Production-Ready
          <br />
          SDKs
        </Heading>
        <Actions>
          <SignUpButton type="button" onClick={() => navigate('/login')}>Sign up </SignUpButton>
          <GetAppButton type="button" onClick={() => navigate('/docs')}> Read Docs </GetAppButton>
        </Actions>
      </Content>
    </Wrapper>
  );
};

export default SpiralHeader;
